/**
 * DeleteSliceController — the destructive-edit machinery (delete /
 * slice / prune / swipe-delete / freehand-cut) extracted from
 * ConstructionTool (#836, epic #828). A tool OWNS a controller
 * instance and DRIVES it by composition (it is NOT a base class),
 * mirroring the `DrawGestureEngine` / `RotationController` pattern:
 * the controller owns the destructive-edit drag STATE and the
 * tap-vs-drag state machines, while everything construction-specific
 * it needs from the owning tool (hit-testing helpers, derived-line
 * predicates, selection) is injected through the `DeleteSliceHost`
 * interface.
 *
 * The controller owns four drag-state fields exclusively (plus the
 * two slop-anchor down-points that pair with them):
 *   - `deletePending`   — a captured delete tap awaiting release;
 *   - `slicePending`    — a captured slice tap awaiting release;
 *   - `sliceDrag`       — the in-flight freehand-cut stroke (#193);
 *   - `deleteDrag`      — the in-flight swipe-delete stroke (#670).
 *
 * Pure path geometry (`prunedPathData`) lives in
 * `src/model/constructionPathData.ts`; the swipe segment planner
 * (`planSwipeSegmentDelete`) stays here because it reads the live
 * rendered scene items + project state.
 *
 * ConstructionTool's `onDown` / `onMove` / `onUp` / `onCancel`
 * delegate the delete / slice / prune / swipe cases to this
 * controller, preserving the exact original dispatch order.
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import {
  isVisible,
  nextConstructionName,
  projectConstructionLines,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import { pruneConstraintsForDeletedLines } from "../model/constraints";
import { pruneInvalidCenterConstraints } from "../model/constraintEnforcement";
import { duplicateOffsetSubtreeForSlice } from "../model/projectOps";
import { repairOutlinesTracingLine } from "../model/constructionOutline";
import {
  offsetSegmentPickAt,
  sameOffsetSegment,
  sliceOffsetBySourceSegments,
  type OffsetSourceSegment,
} from "../model/constructionOffset";
import {
  canonicalizeConstructionLine,
  dropConstructionPrimitive,
} from "../model/constructionLineNormalize";
import {
  deleteAnchor as pathDeleteAnchor,
  deleteHandle as pathDeleteHandle,
  deleteSegment as pathDeleteSegment,
  deleteCurves as pathDeleteCurves,
  type SegmentList,
  type SegmentSpec,
} from "./pathDeletion";
import {
  getPathFirstPoint,
  getPathLastPoint,
  measurePathDataLength,
  parseSubpathToSegmentList,
  prunedPathData,
  segmentListToPathData,
} from "../model/constructionPathData";
import { newId } from "../model/ids";
import { KnifeStroke, type KnifeModifiers } from "./knifeStroke";
import type { EditorContext } from "./EditorContext";
import { cssVar } from "../colors";

/** Dashed stroke colour for the live freehand-cut preview. Matches
 *  the "broken" construction colour used by the swipe-delete preview. */
const SLICE_CUT_STROKE = () => cssVar("--construction-broken");

/** A curve hit-test result (from `DeleteSliceHost.hitConstructionCurve`). */
export interface CurveHit {
  constructionId: string;
  pathIndex: number;
  curveIndex: number;
  curve: paper.Curve;
  t: number;
}

/** A prune target (from `DeleteSliceHost.findPruneTarget`). */
export interface PruneTarget {
  constructionId: string;
  pathIndex: number;
  cutPoint: { x: number; y: number };
  keepSide: "left" | "right";
  overlayPathData: string;
}

/** An anchor / bezier-tip handle hit (from the tool's
 *  `findVisibleHandleHit`), passed into `handleDown`. */
export type HandleHit =
  | {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }
  | {
      kind: "bezier-tip";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      side: "in" | "out";
    };

/** The captured delete-tap target, resolved by
 *  `performConstructionDeletion` on release. */
export type DeletionTarget =
  | {
      kind: "handle";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      side: "in" | "out";
    }
  | {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }
  | {
      kind: "curve";
      constructionId: string;
      pathIndex: number;
      curveIndex: number;
    }
  | {
      kind: "prune";
      constructionId: string;
      pathIndex: number;
      cutPoint: { x: number; y: number };
      keepSide: "left" | "right";
    }
  | {
      kind: "line";
      constructionId: string;
    }
  | {
      // One edge's worth of an offset line (issue #116). `segment`
      // addresses an edge of the offset's SOURCE, not of the offset
      // itself — the offset has no editable edges of its own, and the
      // source edge is what the geometry is re-derived from.
      kind: "offsetSegment";
      constructionId: string;
      segment: OffsetSourceSegment;
    };

/** Everything the DeleteSliceController needs from its owning tool.
 *  The destructive-edit drag-state fields live in the controller;
 *  the hit-testing / derived-line predicates it relies on stay in
 *  ConstructionTool (they're shared with the tool's hover + other
 *  gestures) and are injected through this host. */
export interface DeleteSliceHost {
  // ---- core context -------------------------------------------------
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: SceneSelection): void;

  // ---- shared hit-testing / predicates (stay in ConstructionTool) ---
  /** Nearest visible construction curve under a point, or null. */
  hitConstructionCurve(pt: paper.Point): CurveHit | null;
  /** A hanging-end prune target on the hit curve, or null. */
  findPruneTarget(pt: paper.Point, curveHit: CurveHit): PruneTarget | null;
  /** True for any derived (offset OR mirror) line. */
  isDerivedLineId(id: string): boolean;
  /** True only for offset-derived lines. */
  isOffsetDerivedLineId(id: string): boolean;
  /** True only for mirror-derived (non-offset) lines. */
  isMirrorOnlyDerivedLineId(id: string): boolean;
  /** The line the Delete cursor is over (anchor/handle > curve). */
  lineUnderCursorForDelete(
    p: { x: number; y: number },
    pt: paper.Point,
    handle: HandleHit | null,
  ): { constructionId: string; pathIndex: number } | null;
  /** The editor context, for the knife's snap pipeline (#1013). `KnifeStroke`
   *  needs the snap SETTINGS, the scene's grid and the project's snappable
   *  geometry — which together are just the context, so passing it beats
   *  widening this interface with three accessors that only travel as a set. */
  getContext(): EditorContext;
}

export class DeleteSliceController {
  // ---- exclusively-owned drag state ---------------------------------

  /** Tap captured on `onDown` while in the "delete" edit action,
   *  along with the hit context the tap landed on. Resolved into
   *  a path-deletion commit on `onUp` if the user didn't drag past
   *  the slop. The `target` discriminator drives which deletion
   *  helper runs: bezier handle dot → `deleteHandle`, anchor dot
   *  → `deleteAnchor`, curve stroke → `deleteSegment`. */
  private deletePending: {
    x: number;
    y: number;
    target: DeletionTarget;
  } | null = null;

  /** Tap captured on `onDown` while in the "slice" edit action. The
   *  target is always a curve hit — slice doesn't act on anchors or
   *  handles. Resolved into a path-split commit on `onUp` if the user
   *  didn't drag past the slop. */
  private slicePending: {
    x: number;
    y: number;
    target: {
      constructionId: string;
      pathIndex: number;
      curveIndex: number;
      t: number;
    };
  } | null = null;

  /** Sliding state for the freehand-cut drag: the user holds the
   *  knife and drags across the canvas, producing a polyline cut
   *  path that splits every construction line it crosses (at each
   *  crossing). `samples` accumulates project-mm cursor positions
   *  from drag-start through the latest `onMove`. `previewItem` is
   *  the dashed paper.Path overlay rendered live so the user can
   *  see where they're cutting. Engaged when the cursor moves
   *  more than the slop threshold from `sliceDragStart`. */
  private sliceDrag: {
    stroke: KnifeStroke;
    previewItem: paper.Item | null;
  } | null = null;

  /** Down-point captured in slice mode regardless of whether the
   *  tap landed on a curve. Used purely as the slop-detection
   *  anchor — if the cursor moves more than the threshold before
   *  release, the gesture is promoted from tap-slice to
   *  freehand-cut drag. Cleared on `onUp` / cancel paths. */
  private sliceDragStart: { x: number; y: number } | null = null;

  /** The snapped knife stroke (#1013), alive from the slice press through
   *  release. It starts BEFORE `sliceDrag` because the press point is one of
   *  the two endpoints the snap pipeline resolves — by the time the drag passes
   *  the slop, the cursor has already left it. */
  private sliceStroke: KnifeStroke | null = null;

  /** Swipe-delete drag state (issue #670 follow-up): dragging the
   *  Delete sub-sub-tool sweeps a "knife" stroke that, on release,
   *  trims the dangling stub off every line it crosses (or, with
   *  `shiftKey`, deletes each crossed line whole). Mirrors
   *  `sliceDrag`: `samples` accumulates the stroke, `previewItem` is
   *  the live red dashed overlay. */
  private deleteDrag: {
    samples: Array<{ x: number; y: number }>;
    previewItem: paper.Item | null;
    shiftKey: boolean;
  } | null = null;

  /** Down-point captured in delete mode (any hit or empty space) —
   *  the slop anchor that promotes a tap into a swipe-delete drag.
   *  Carries the Shift state held at press so the whole gesture stays
   *  consistent even if the key changes mid-drag. */
  private deleteDragStart: {
    x: number;
    y: number;
    shiftKey: boolean;
  } | null = null;

  constructor(private host: DeleteSliceHost) {}

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** onDown delegation for the slice / delete edit actions. Captures
   *  the tap target + slop anchor; the actual destructive commit
   *  fires on release (or the gesture promotes to a drag on move).
   *  Preserves the exact original in-order dispatch. */
  handleDown(
    p: { x: number; y: number },
    pt: paper.Point,
    s: PointerSample,
    action: "slice" | "delete",
    handle: HandleHit | null,
  ): void {
    if (action === "slice") {
      // Slice sub-sub-tool. Two gestures share one chord: a *tap*
      // on a construction curve splits that line at the click
      // point (single-point slice), while a *drag* anywhere on
      // the canvas draws a freehand cut path that splits every
      // visible construction line it crosses at each crossing.
      // Capture both anchors here on mouseDown — `slicePending`
      // carries the tap target (if any), `sliceDragStart` is the
      // slop reference. `onMove` decides which gesture this is:
      // movement past the slop threshold drops `slicePending`
      // and engages `sliceDrag` (freehand cut). `onUp` then
      // commits whichever is still set.
      this.sliceDragStart = { x: p.x, y: p.y };
      // Begin the snapped knife stroke now, not on promotion: the PRESS point
      // is one of the two the snap pipeline resolves (#1013), and by the time
      // the drag passes the slop the cursor has already left it.
      this.sliceStroke = new KnifeStroke();
      this.sliceStroke.begin(this.host.getContext(), p, s);
      const curveHit = this.host.hitConstructionCurve(pt);
      if (curveHit) {
        if (this.host.isDerivedLineId(curveHit.constructionId)) return;
        this.slicePending = {
          x: p.x,
          y: p.y,
          target: {
            constructionId: curveHit.constructionId,
            pathIndex: curveHit.pathIndex,
            curveIndex: curveHit.curveIndex,
            t: curveHit.t,
          },
        };
      }
      return;
    }
    // Slop anchor for a swipe-delete drag (issue #670 follow-up):
    // captured whether or not the press landed on a line, so a
    // stroke that starts in empty space (e.g. sweeping along the
    // outside of a shape) still engages.
    this.deleteDragStart = { x: p.x, y: p.y, shiftKey: !!s.shiftKey };
    // Holding Shift turns ANY tap on a line into a whole-line
    // delete, regardless of the sub-target under the cursor
    // (issue #670 follow-up). Mirror-derived lines stay refused.
    if (s.shiftKey) {
      const lineHit = this.host.lineUnderCursorForDelete(p, pt, handle);
      if (
        lineHit &&
        !this.host.isMirrorOnlyDerivedLineId(lineHit.constructionId)
      ) {
        this.deletePending = {
          x: p.x,
          y: p.y,
          target: { kind: "line", constructionId: lineHit.constructionId },
        };
      }
      return;
    }
    // Delete sub-sub-tool: capture the most-specific hit at the
    // tap point. Priority: bezier tip > anchor > curve stroke.
    // Empty space is a no-op. The actual deletion fires on
    // `onUp` if the user didn't drag past the slop.
    //
    // Derived-line policy:
    //  - **Offset-derived** lines route ANY hit on the line through
    //    `offsetDeletionTarget`: the tap switches OFF the source edge
    //    the cursor is over, so the offset stops following that stretch
    //    (issue #116). Anchors and handles land there too — they belong
    //    to re-derived geometry, so there is nothing piecewise to edit,
    //    only a stretch to drop. Offsets with no addressable stretch
    //    (single-edge offsets, or a source that vanished) fall back to
    //    the whole-line delete of issue #327, which Shift still forces
    //    everywhere.
    //  - **Mirror-derived** lines stay refused (tracked
    //    separately) via `isDerivedLineId`.
    if (handle?.kind === "bezier-tip") {
      if (this.host.isOffsetDerivedLineId(handle.constructionId)) {
        this.deletePending = {
          x: p.x,
          y: p.y,
          target: this.offsetDeletionTarget(handle.constructionId, p),
        };
        return;
      }
      if (this.host.isDerivedLineId(handle.constructionId)) return;
      this.deletePending = {
        x: p.x,
        y: p.y,
        target: {
          kind: "handle",
          constructionId: handle.constructionId,
          pathIndex: handle.pathIndex,
          segmentIndex: handle.segmentIndex,
          side: handle.side,
        },
      };
      return;
    }
    if (handle?.kind === "anchor") {
      if (this.host.isOffsetDerivedLineId(handle.constructionId)) {
        this.deletePending = {
          x: p.x,
          y: p.y,
          target: this.offsetDeletionTarget(handle.constructionId, p),
        };
        return;
      }
      if (this.host.isDerivedLineId(handle.constructionId)) return;
      this.deletePending = {
        x: p.x,
        y: p.y,
        target: {
          kind: "anchor",
          constructionId: handle.constructionId,
          pathIndex: handle.pathIndex,
          segmentIndex: handle.segmentIndex,
        },
      };
      return;
    }
    const curveHit = this.host.hitConstructionCurve(pt);
    if (curveHit) {
      if (this.host.isOffsetDerivedLineId(curveHit.constructionId)) {
        this.deletePending = {
          x: p.x,
          y: p.y,
          target: this.offsetDeletionTarget(curveHit.constructionId, p),
        };
        return;
      }
      if (this.host.isDerivedLineId(curveHit.constructionId)) return;
      // Prefer a hanging-end prune when the cursor's on a stub past a
      // crossing (issue #670); otherwise delete the whole segment.
      const prune = this.host.findPruneTarget(pt, curveHit);
      if (prune) {
        this.deletePending = {
          x: p.x,
          y: p.y,
          target: {
            kind: "prune",
            constructionId: prune.constructionId,
            pathIndex: prune.pathIndex,
            cutPoint: prune.cutPoint,
            keepSide: prune.keepSide,
          },
        };
        return;
      }
      this.deletePending = {
        x: p.x,
        y: p.y,
        target: {
          kind: "curve",
          constructionId: curveHit.constructionId,
          pathIndex: curveHit.pathIndex,
          curveIndex: curveHit.curveIndex,
        },
      };
      return;
    }
  }

  /** onMove delegation: advance whichever swipe / slice drag or
   *  pending tap is live. Returns true when a delete/slice gesture
   *  consumed the move (matching the original block that `return`ed
   *  from each branch). */
  handleMove(p: { x: number; y: number }, s?: KnifeModifiers): boolean {
    if (this.deleteDrag) {
      // Active swipe-delete — append every cursor sample to the
      // knife stroke and refresh the red dashed preview.
      this.deleteDrag.samples.push({ x: p.x, y: p.y });
      this.refreshDeleteSwipePreview();
      return true;
    }
    if (this.deleteDragStart) {
      const dx = p.x - this.deleteDragStart.x;
      const dy = p.y - this.deleteDragStart.y;
      if (Math.hypot(dx, dy) > 3) {
        // Slop passed: promote the tap into a swipe-delete drag. Drop
        // the tap target + its red hover overlay, then seed the sample
        // buffer with start + current so the preview has length from
        // the first frame.
        this.deletePending = null;
        this.host.getScene().setConstructionDeleteHover(null);
        this.deleteDrag = {
          samples: [
            { x: this.deleteDragStart.x, y: this.deleteDragStart.y },
            { x: p.x, y: p.y },
          ],
          previewItem: null,
          shiftKey: this.deleteDragStart.shiftKey,
        };
        this.refreshDeleteSwipePreview();
      }
      return true;
    }
    if (this.deletePending) {
      const dx = p.x - this.deletePending.x;
      const dy = p.y - this.deletePending.y;
      if (Math.hypot(dx, dy) > 3) this.deletePending = null;
      return true;
    }
    if (this.sliceDrag) {
      // Active freehand cut — advance the snapped stroke and refresh the
      // dashed preview.
      this.sliceDrag.stroke.extend(this.host.getContext(), p, s ?? {});
      this.refreshFreehandSlicePreview();
      return true;
    }
    if (this.sliceDragStart) {
      this.sliceStroke?.extend(this.host.getContext(), p, s ?? {});
      // Slop is measured on RAW cursor travel: a press that snapped to a vertex
      // a few mm away would otherwise read as a drag the user never made.
      if ((this.sliceStroke?.rawTravelMm() ?? 0) > 3) {
        // Slop passed: this is a freehand cut drag, not a tap. Drop any tap
        // target — we're committing to the drag gesture — and hand the stroke
        // (which has been accumulating since the press) to the drag.
        this.slicePending = null;
        this.sliceDrag = {
          stroke: this.sliceStroke ?? new KnifeStroke(),
          previewItem: null,
        };
        this.refreshFreehandSlicePreview();
      }
      return true;
    }
    if (this.slicePending) {
      const dx = p.x - this.slicePending.x;
      const dy = p.y - this.slicePending.y;
      if (Math.hypot(dx, dy) > 3) this.slicePending = null;
      return true;
    }
    return false;
  }

  /** onUp delegation: commit whichever swipe / slice drag or pending
   *  tap is live, then clear its state. Returns true when a delete /
   *  slice gesture consumed the release (the caller then refreshes the
   *  edit cursor). A bare release that only clears a leftover slop
   *  anchor returns false so the tool's dispatch continues, exactly as
   *  the original fall-through did. */
  handleUp(p: { x: number; y: number }): boolean {
    if (this.deleteDrag) {
      // Swipe-delete release: trim / remove every line the knife
      // stroke crossed, then clear all swipe state.
      const samples = this.deleteDrag.samples;
      const shiftKey = this.deleteDrag.shiftKey;
      this.clearDeleteDrag();
      this.deletePending = null;
      this.deleteDragStart = null;
      this.host.getScene().setConstructionDeleteHover(null);
      this.performSwipeDelete(samples, shiftKey);
      return true;
    }
    if (this.deletePending) {
      // Tap survived the slop window — perform the deletion the
      // captured target dictates. Clear the red overlay first; the
      // target it was pointing at is about to disappear.
      const target = this.deletePending.target;
      this.deletePending = null;
      this.deleteDragStart = null;
      this.host.getScene().setConstructionDeleteHover(null);
      this.performConstructionDeletion(target);
      return true;
    }
    // Bare release in delete mode (empty-space click that never became
    // a swipe) — clear the slop anchor so the next gesture starts fresh.
    if (this.deleteDragStart) {
      this.deleteDragStart = null;
    }
    if (this.sliceDrag) {
      // Freehand cut release: hand the resolved polyline to the
      // intersect-and-split routine, then clear all slice state.
      const samples = this.sliceDrag.stroke
        .points()
        .map((q) => ({ x: q.x, y: q.y }));
      this.clearSliceDrag();
      this.clearDeleteDrag();
      // Drop slicePending — the gesture turned into a drag, so the
      // tap target is no longer relevant.
      this.slicePending = null;
      this.performConstructionFreehandSlice(samples);
      return true;
    }
    if (this.slicePending) {
      const target = this.slicePending.target;
      this.slicePending = null;
      this.sliceDragStart = null;
      this.sliceStroke?.end(this.host.getContext());
      this.sliceStroke = null;
      this.performConstructionSlice(target);
      return true;
    }
    // Bare release in slice mode (empty-space click that never
    // became a drag) — clear the slop anchor so the next
    // gesture starts fresh.
    if (this.sliceDragStart) {
      this.sliceDragStart = null;
      this.sliceStroke?.end(this.host.getContext());
      this.sliceStroke = null;
    }
    return false;
  }

  /** Re-resolve the live cut when a modifier changes mid-drag (#1013), so the
   *  straight-vs-freehand switch happens under the user's finger rather than on
   *  the next pointer move. Returns true when a slice drag consumed it. */
  handleModifiers(m: KnifeModifiers): boolean {
    if (!this.sliceStroke) return false;
    this.sliceStroke.replayModifiers(this.host.getContext(), m);
    if (this.sliceDrag) this.refreshFreehandSlicePreview();
    return this.sliceDrag !== null;
  }

  /** Drop every pending tap + in-flight drag. Called from the tool's
   *  sub-mode / edit-action switches and its onCancel path. Does NOT
   *  touch the scene delete-hover overlay — the caller owns that. */
  reset(): void {
    this.deletePending = null;
    this.slicePending = null;
    this.clearSliceDrag();
    this.clearDeleteDrag();
  }

  // -------------------------------------------------------------------
  // Deletion (tap: handle / anchor / curve / prune / whole line)
  // -------------------------------------------------------------------

  /** Apply a delete-sub-sub-tool target — handle / anchor / curve
   *  — by parsing pathData into a `SegmentList`, running the pure
   *  deletion helper, and committing the result(s). Segment
   *  deletions can produce 0, 1, or 2 lines (the original splits or
   *  one side dies as a single-anchor leftover); anchor and handle
   *  deletions always produce one line. Drops the primitive in the
   *  destructive sense (no round-trip restore eligibility) since
   *  deleting geometry is fundamentally not reversible via the
   *  cycle. Clears `smoothAnchors` / `bezierMemory` for safety —
   *  segment indices may shift after the deletion. */
  /** What a Delete tap on an offset line at `p` should do: drop the
   *  stretch under the cursor if that stretch is addressable, else fall
   *  back to removing the whole offset (issue #327's behaviour, which
   *  is still the only option for a single-edge offset). */
  private offsetDeletionTarget(
    constructionId: string,
    p: { x: number; y: number },
  ): DeletionTarget {
    const pick = offsetSegmentPickAt(
      this.host.getProject(),
      constructionId,
      p,
      this.host.getScope(),
    );
    return pick
      ? { kind: "offsetSegment", constructionId, segment: pick.segment }
      : { kind: "line", constructionId };
  }

  /** Strip a construction line from the project and purge any snap
   *  constraint that referenced it. Shared by the whole-line delete and
   *  by the offset-segment delete that removes an offset's last
   *  remaining stretch. */
  private deleteWholeConstructionLine(id: string, label: string): void {
    this.host.commit(label, (proj) => ({
      ...proj,
      constructionLines: (proj.constructionLines ?? []).filter(
        (l) => l.id !== id,
      ),
      constraints: pruneConstraintsForDeletedLines(
        proj.constraints,
        new Set([id]),
      ),
    }));
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: null,
    });
  }

  /** Switch OFF one source edge of an offset line (issue #116): the
   *  offset stops following that edge and the matching stretch drops
   *  out of its geometry on the next derive. The edge stays listed in
   *  the offset's properties panel so it can be switched back on.
   *
   *  Removing the LAST stretch leaves nothing to draw, so that case
   *  deletes the line outright rather than leaving an invisible row in
   *  the layers panel. "Nothing left" is decided by actually cutting
   *  the edge out of the current geometry — which also covers the
   *  cases a segment count can't see, like a source edge whose offset
   *  run was already dropped as invalid. */
  private performOffsetSegmentDeletion(
    line: ConstructionLine,
    segment: OffsetSourceSegment,
  ): void {
    const link = line.offsetSource;
    if (!link) return;
    const source = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === link.sourceId,
    );
    if (!source) return;
    const already = link.excludedSegments ?? [];
    if (already.some((e) => sameOffsetSegment(e, segment))) return;
    const remaining = sliceOffsetBySourceSegments(
      line.pathData,
      source.pathData,
      (seg) => !sameOffsetSegment(seg, segment),
      this.host.getScope(),
    );
    if (remaining === null) {
      this.deleteWholeConstructionLine(line.id, "Delete offset");
      return;
    }
    const id = line.id;
    const excludedSegments = [...already, segment];
    const scope = this.host.getScope();
    this.host.commit("Delete offset segment", (proj) =>
      // `remaining` is written straight onto the line rather than waiting for
      // the derive pass at the end of the commit, so the repair below sees the
      // geometry that survived. The deriver recomputes the same shape from the
      // source a moment later.
      repairOutlinesTracingLine(
        proj,
        {
          ...proj,
          constructionLines: (proj.constructionLines ?? []).map((l) =>
            l.id === id
              ? {
                  ...l,
                  pathData: remaining,
                  offsetSource: { ...link, excludedSegments },
                }
              : l,
          ),
        },
        id,
        scope,
      ),
    );
  }

  private performConstructionDeletion(target: DeletionTarget): void {
    const project = this.host.getProject();
    const line = projectConstructionLines(project).find(
      (l) => l.id === target.constructionId,
    );
    if (!line) return;
    if (target.kind === "prune") {
      this.performConstructionPrune(line, target);
      return;
    }
    if (target.kind === "offsetSegment") {
      this.performOffsetSegmentDeletion(line, target.segment);
      return;
    }
    if (target.kind === "line") {
      // Whole-line delete (issue #327) — used when an offset-derived
      // line is the tap target. Same shape as the "results.length
      // === 0" path further down for non-derived lines: strip the
      // line from `constructionLines` and prune any snap constraint
      // that referenced it. Lines whose `offsetSource.sourceId`
      // pointed at this id (chained offsets) are handled by the
      // offset connection's commit-pipeline cycle / cleanup pass —
      // the orphan-source row falls out of the derive() call when
      // its source vanishes.
      this.deleteWholeConstructionLine(target.constructionId, "Delete offset");
      return;
    }
    const scope = this.host.getScope();
    const list = parseSubpathToSegmentList(
      line.pathData,
      target.pathIndex,
      scope,
    );
    if (!list) return;

    let results: SegmentList[];
    let label: string;
    if (target.kind === "handle") {
      results = pathDeleteHandle(list, target.segmentIndex, target.side);
      label = "Delete bezier handle";
    } else if (target.kind === "anchor") {
      results = pathDeleteAnchor(list, target.segmentIndex);
      label = "Delete anchor";
    } else {
      results = pathDeleteSegment(list, target.curveIndex);
      label = "Delete segment";
    }

    if (results.length === 0) {
      // Whole path was the deleted thing — drop the line entirely
      // and purge any constraints that referenced it (the partner
      // line keeps its anchors but the constraint becomes
      // meaningless without both participants).
      const id = target.constructionId;
      this.host.commit(label, (proj) => ({
        ...proj,
        constructionLines: (proj.constructionLines ?? []).filter(
          (l) => l.id !== id,
        ),
        constraints: pruneConstraintsForDeletedLines(
          proj.constraints,
          new Set([id]),
        ),
      }));
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        constructionId: null,
      });
      return;
    }

    // Serialise each result to pathData via paper. The first result
    // replaces the original line; any extras become brand-new lines
    // (the segment-split case). Drop primitive metadata + clear the
    // cycle's per-anchor memory since indices shift after deletion.
    const pathDatas = results.map((r) => segmentListToPathData(r, scope));
    const firstId = target.constructionId;
    const firstKind: "polyline" | "polygon" = results[0]!.closed
      ? "polygon"
      : "polyline";
    this.host.commit(label, (proj) => {
      const updated = (proj.constructionLines ?? []).map((l) => {
        if (l.id !== firstId) return l;
        return dropConstructionPrimitive(
          l,
          proj,
          {
            pathData: pathDatas[0]!,
            // The result's closed flag drives the kind label —
            // a deleted segment can flip a closed primitive into
            // an open polyline. Pass it explicitly so
            // dropConstructionPrimitive's "default to polygon"
            // fallback doesn't kick in.
            kind: firstKind,
            // Indices shifted — drop both lock and handle memory.
            smoothAnchors: undefined,
            bezierMemory: undefined,
          },
          // Destructive — no round-trip restore eligibility.
          // Pass scope so the canonicalization pass at the bottom
          // of dropConstructionPrimitive can promote a single-
          // segment leftover into a Line / Curve.
          { preserveOrigin: false, scope },
        );
      });
      // Tail results become fresh polylines / polygons / lines /
      // curves with their own ids. The polyline / polygon kind comes
      // from the result's closed flag; `canonicalizeConstructionLine`
      // then promotes single-segment open results to the Line
      // primitive or "freehand" (Curve) kind to match the actual
      // geometry.
      for (let i = 1; i < results.length; i++) {
        const tail = results[i]!;
        const kind: "polyline" | "polygon" = tail.closed
          ? "polygon"
          : "polyline";
        const tailLine: ConstructionLine = {
          id: newId("con"),
          name: nextConstructionName(
            { ...proj, constructionLines: updated },
            kind === "polygon" ? "Polygon" : "Polyline",
          ),
          pathData: pathDatas[i]!,
          kind,
        };
        updated.push(
          canonicalizeConstructionLine(tailLine, scope, {
            ...proj,
            constructionLines: updated,
          }),
        );
      }
      // Destroying geometry renumbers the line's anchors — which is what
      // anything TRACING it addressed its segments by — so re-point those
      // traces at what survived (#1029), which needs the project from both
      // sides of the edit. Anything unrepairable detaches instead.
      return repairOutlinesTracingLine(
        proj,
        {
          ...proj,
          constructionLines: updated,
          constraints: pruneInvalidCenterConstraints(updated, proj.constraints),
        },
        firstId,
        scope,
      );
    });
  }

  /** Prune a hanging end off a construction line (issue #670). The
   *  line crosses another line and pokes past it; the tap trims that
   *  overhanging stub by splitting the subpath at the crossing
   *  (`cutPoint`) and keeping only `keepSide`. Like half a slice: one
   *  side is discarded rather than spun off into a new line.
   *
   *  The line keeps its id but drops its parametric primitive,
   *  `smoothAnchors`, and bezier handle memory — segment indices
   *  shift after the cut. A straight leftover re-canonicalizes back
   *  into a Line via `dropConstructionPrimitive`'s scope pass. */
  private performConstructionPrune(
    line: ConstructionLine,
    target: {
      constructionId: string;
      pathIndex: number;
      cutPoint: { x: number; y: number };
      keepSide: "left" | "right";
    },
  ): void {
    const scope = this.host.getScope();
    const newPathData = prunedPathData(
      scope,
      line.pathData,
      target.pathIndex,
      target.cutPoint,
      target.keepSide,
    );
    if (!newPathData) return;

    const id = target.constructionId;
    const pruneScope = this.host.getScope();
    this.host.commit("Prune construction line", (proj) => {
      const updated = (proj.constructionLines ?? []).map((l) => {
        if (l.id !== id) return l;
        return dropConstructionPrimitive(
          l,
          proj,
          {
            pathData: newPathData,
            kind: "polyline",
            smoothAnchors: undefined,
            bezierMemory: undefined,
          },
          // Scope lets the canonicalization promote a straight 2-anchor
          // leftover back into a Line primitive — pruning a line should
          // leave a line.
          { preserveOrigin: false, scope },
        );
      });
      // Destroying geometry renumbers the line's anchors — which is what
      // anything TRACING it addressed its segments by — so re-point those
      // traces at what survived (#1029), which needs the project from both
      // sides of the edit. Anything unrepairable detaches instead.
      return repairOutlinesTracingLine(
        proj,
        {
          ...proj,
          constructionLines: updated,
          constraints: pruneInvalidCenterConstraints(updated, proj.constraints),
        },
        id,
        pruneScope,
      );
    });
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: null,
    });
  }

  // -------------------------------------------------------------------
  // Swipe-delete (drag: trim / whole-line across every crossed line)
  // -------------------------------------------------------------------

  /** Swipe-delete commit (issue #670 follow-up). The user dragged the
   *  Delete knife across the canvas; the polyline of pointer samples
   *  is a stroke that, for every visible line it crosses, deletes the
   *  segment(s) it passed through (default) or removes the whole line
   *  (`shiftKey`). All edits land in a single commit so one Undo
   *  reverses the whole sweep.
   *
   *  Segment policy: each crossed line is first split at its
   *  intersections with OTHER geometry, so the deletion boundaries
   *  are anchors AND junctions — the piece removed is exactly the one
   *  the knife crossed, trimmed at the nearest junction. A line with
   *  no junctions loses just the curve the knife hit (which, for a
   *  plain 2-point line, is the whole line). */
  private performSwipeDelete(
    samples: Array<{ x: number; y: number }>,
    shiftKey: boolean,
  ): void {
    if (samples.length < 2) return;
    const scope = this.host.getScope();
    let knife: paper.Path | null = null;
    try {
      knife = new scope.Path({ insert: false });
      for (const s of samples) knife.add(new scope.Point(s.x, s.y));
    } catch {
      return;
    }
    const project = this.host.getProject();
    const removeIds = new Set<string>();
    // lineId → resulting pieces; an empty array means the crossed
    // geometry vanished entirely (treated as a whole-line delete).
    const replacements = new Map<string, string[]>();
    for (const line of projectConstructionLines(project)) {
      if (!isVisible(line)) continue;
      // Mirror-derived lines can't be deleted directly (consistent
      // with tap / Shift); offsets re-derive, so they only ever go
      // whole, never segment-trimmed.
      if (this.host.isMirrorOnlyDerivedLineId(line.id)) continue;
      const item = this.host.getScene().getConstructionLineItem(line.id);
      if (!item) continue;
      let inters: paper.CurveLocation[] = [];
      try {
        inters = knife.getIntersections(item as paper.PathItem);
      } catch {
        continue;
      }
      if (inters.length === 0) continue;
      if (shiftKey || this.host.isOffsetDerivedLineId(line.id)) {
        removeIds.add(line.id);
        continue;
      }
      const pieces = this.planSwipeSegmentDelete(line, knife, scope);
      if (pieces) replacements.set(line.id, pieces);
    }
    knife.remove();
    if (removeIds.size === 0 && replacements.size === 0) return;

    const swipeScope = this.host.getScope();
    const swipeIds = [...removeIds, ...replacements.keys()];
    this.host.commit("Delete across lines", (proj) => {
      const updated: ConstructionLine[] = [];
      const deleted = new Set(removeIds);
      for (const l of proj.constructionLines ?? []) {
        if (removeIds.has(l.id)) continue;
        const pieces = replacements.get(l.id);
        if (!pieces) {
          updated.push(l);
          continue;
        }
        if (pieces.length === 0) {
          deleted.add(l.id);
          continue;
        }
        // First surviving piece keeps the line's id; the rest become
        // fresh polylines (an interior cut can split a line in two).
        updated.push(
          dropConstructionPrimitive(
            l,
            proj,
            {
              pathData: pieces[0]!,
              kind: "polyline",
              smoothAnchors: undefined,
              bezierMemory: undefined,
            },
            { preserveOrigin: false, scope },
          ),
        );
        for (let i = 1; i < pieces.length; i++) {
          const tail: ConstructionLine = {
            id: newId("con"),
            name: nextConstructionName(
              { ...proj, constructionLines: updated },
              "Polyline",
            ),
            pathData: pieces[i]!,
            kind: "polyline",
          };
          updated.push(
            canonicalizeConstructionLine(tail, scope, {
              ...proj,
              constructionLines: updated,
            }),
          );
        }
      }
      // Destroying geometry renumbers the line's anchors — which is what
      // anything TRACING it addressed its segments by — so re-point those
      // traces at what survived (#1029), which needs the project from both
      // sides of the edit. Anything unrepairable detaches instead.
      return repairOutlinesTracingLine(
        proj,
        {
          ...proj,
          constructionLines: updated,
          constraints: pruneInvalidCenterConstraints(
            updated,
            pruneConstraintsForDeletedLines(proj.constraints, deleted),
          ),
        },
        swipeIds,
        swipeScope,
      );
    });
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: null,
    });
  }

  /** Plan a swipe segment-delete for one line: split the subpath the
   *  knife crosses at its junctions with other geometry, then drop the
   *  curve(s) the knife actually passed through. Returns the surviving
   *  pieces' pathData (first keeps the line's id; an empty array means
   *  the whole subpath went). `null` when the knife didn't resolve to a
   *  deletable curve. Reads the live rendered scene items + project
   *  state, so it stays on the controller (not a pure model helper). */
  private planSwipeSegmentDelete(
    line: ConstructionLine,
    knife: paper.Path,
    scope: paper.PaperScope,
  ): string[] | null {
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    } catch {
      return null;
    }
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];

    // The first subpath the knife crosses is the one we edit.
    let crossedIndex = -1;
    for (let i = 0; i < subpaths.length; i++) {
      let ki: paper.CurveLocation[] = [];
      try {
        ki = subpaths[i]!.getIntersections(knife);
      } catch {
        ki = [];
      }
      if (ki.length > 0) {
        crossedIndex = i;
        break;
      }
    }
    if (crossedIndex < 0) {
      cp.remove();
      return null;
    }
    const sub = subpaths[crossedIndex]!;
    const EPS = 1e-3;
    const subLen = sub.length;

    // Knife crossing offsets along the subpath (strictly interior).
    let knifeInters: paper.CurveLocation[] = [];
    try {
      knifeInters = sub.getIntersections(knife);
    } catch {
      knifeInters = [];
    }
    const knifeOffsets = knifeInters
      .map((it) => it.offset)
      .filter((o) => o > EPS && o < subLen - EPS);
    if (knifeOffsets.length === 0) {
      cp.remove();
      return null;
    }

    // Offsets of the existing anchors (cumulative curve lengths).
    const anchorOffsets: number[] = [0];
    for (const c of sub.curves) {
      anchorOffsets.push(anchorOffsets[anchorOffsets.length - 1]! + c.length);
    }
    // Offsets where this line crosses OTHER visible geometry — the
    // junctions the trim must stop at.
    const project = this.host.getProject();
    const junctionOffsets: number[] = [];
    for (const other of projectConstructionLines(project)) {
      if (other.id === line.id) continue;
      if (!isVisible(other)) continue;
      const otherItem = this.host.getScene().getConstructionLineItem(other.id);
      if (!otherItem) continue;
      try {
        for (const it of sub.getIntersections(otherItem as paper.PathItem)) {
          if (it.offset > EPS && it.offset < subLen - EPS) {
            junctionOffsets.push(it.offset);
          }
        }
      } catch {
        /* ignore a bad intersection probe */
      }
    }

    // The deletion boundaries are anchors AND junctions. The piece the
    // knife crossed is the stretch between the two nearest boundaries —
    // delete exactly that. CRITICAL (issue #670 follow-up): only split
    // at the boundaries of the deleted piece(s); junctions that just
    // pass through a SURVIVING stretch are left un-split so a single
    // segment doesn't fragment into pseudo-segments.
    const boundaries = [...anchorOffsets, ...junctionOffsets].sort(
      (a, b) => a - b,
    );
    const isAnchor = (o: number): boolean =>
      anchorOffsets.some((a) => Math.abs(a - o) < EPS);
    const pieceKeys = new Set<string>();
    const delPieces: Array<{ lo: number; hi: number }> = [];
    for (const ko of knifeOffsets) {
      let lo = 0;
      let hi = subLen;
      for (const b of boundaries) {
        if (b <= ko + EPS) lo = b;
        else {
          hi = b;
          break;
        }
      }
      const key = `${lo.toFixed(4)}_${hi.toFixed(4)}`;
      if (!pieceKeys.has(key)) {
        pieceKeys.add(key);
        delPieces.push({ lo, hi });
      }
    }

    // Split only at the junction boundaries of the deleted pieces
    // (anchors are already there). Descending so earlier offsets stay
    // valid as divideAt mutates the path.
    const splitOffsets = Array.from(
      new Set(
        delPieces
          .flatMap((p) => [p.lo, p.hi])
          .filter((o) => o > EPS && o < subLen - EPS && !isAnchor(o))
          .map((o) => Number(o.toFixed(4))),
      ),
    ).sort((a, b) => b - a);
    for (const o of splitOffsets) {
      const loc = sub.getLocationAt(o);
      if (loc) {
        try {
          sub.divideAt(loc);
        } catch {
          /* divide failed — skip this junction */
        }
      }
    }

    // The curve covering each deleted piece (its midpoint resolves to a
    // single curve now that the piece is bounded by anchors).
    const del = new Set<number>();
    for (const p of delPieces) {
      const loc = sub.getLocationAt((p.lo + p.hi) / 2);
      if (loc && loc.curve) del.add(loc.curve.index);
    }
    if (del.size === 0) {
      cp.remove();
      return null;
    }

    const list: SegmentList = {
      closed: sub.closed,
      segments: sub.segments.map(
        (s): SegmentSpec => ({
          point: { x: s.point.x, y: s.point.y },
          handleIn: { x: s.handleIn.x, y: s.handleIn.y },
          handleOut: { x: s.handleOut.x, y: s.handleOut.y },
        }),
      ),
    };
    const results = pathDeleteCurves(list, [...del]);
    const crossedPieces = results.map((r) => segmentListToPathData(r, scope));
    const otherSubsPathData = subpaths
      .filter((_, i) => i !== crossedIndex)
      .map((s) => s.pathData)
      .filter(Boolean);
    cp.remove();

    if (crossedPieces.length === 0) {
      // The crossed subpath disappeared; keep any sibling subpaths.
      return otherSubsPathData.length > 0 ? [otherSubsPathData.join(" ")] : [];
    }
    // Sibling subpaths ride along with the first surviving piece.
    const pieces: string[] = [
      [...otherSubsPathData, crossedPieces[0]!].join(" "),
    ];
    for (let i = 1; i < crossedPieces.length; i++)
      pieces.push(crossedPieces[i]!);
    return pieces;
  }

  // -------------------------------------------------------------------
  // Slice (tap: single-point split)
  // -------------------------------------------------------------------

  /** Split a construction line at the clicked point on one of its
   *  curves (issue #193 — "Slice" sub-sub-tool). Hands the heavy
   *  geometry math to paper.js's `splitAt`, which subdivides the
   *  curve via De Casteljau and creates a new anchor at the cut.
   *
   *  Open subpath → two construction lines: the original keeps its
   *  id with the left half (plus any sibling subpaths of the
   *  compound), the right half becomes a brand-new line.
   *  Closed subpath → one construction line: paper opens the loop
   *  at the cut, so the original line stays but is no longer closed.
   *
   *  In either case the line drops its parametric primitive
   *  (rect / ellipse / pill / etc. don't survive a cut), its
   *  `smoothAnchors` lock list, and its bezier handle memory —
   *  segment indices shift after the split so the per-anchor
   *  metadata can't be carried over verbatim. */
  private performConstructionSlice(target: {
    constructionId: string;
    pathIndex: number;
    curveIndex: number;
    t: number;
  }): void {
    const project = this.host.getProject();
    const line = projectConstructionLines(project).find(
      (l) => l.id === target.constructionId,
    );
    if (!line) return;
    const scope = this.host.getScope();
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    } catch {
      return;
    }
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    if (target.pathIndex < 0 || target.pathIndex >= subpaths.length) {
      cp.remove();
      return;
    }
    const targetSub = subpaths[target.pathIndex]!;
    const curve = targetSub.curves[target.curveIndex];
    if (!curve) {
      cp.remove();
      return;
    }
    // Skip degenerate slices at the very start / end of an open
    // path — the cut would either be a no-op or produce a 1-anchor
    // dangling "line".
    const EPS = 1e-4;
    if (
      !targetSub.closed &&
      ((target.curveIndex === 0 && target.t < EPS) ||
        (target.curveIndex === targetSub.curves.length - 1 &&
          target.t > 1 - EPS))
    ) {
      cp.remove();
      return;
    }
    const loc = curve.getLocationAtTime(target.t);
    if (!loc) {
      cp.remove();
      return;
    }
    // paper's splitAt: open path → mutates `targetSub` to the left
    // half and inserts the right half as a sibling Path of
    // `targetSub` in its parent (here, `cp`). closed path → opens
    // the loop at `loc` in place and returns null. The defensive
    // `!== targetSub` guards against paper variants that hand back
    // the same path.
    const splitResult = (targetSub as paper.Path).splitAt(loc);
    const second =
      splitResult &&
      splitResult !== targetSub &&
      splitResult.segments &&
      splitResult.segments.length >= 2
        ? (splitResult as paper.Path)
        : null;
    // CRITICAL ordering: capture `secondPathData` first (while
    // `second` is still alive), then detach `second` from `cp` so
    // `cp.pathData` reflects just the left half + sibling
    // subpaths. Without the detach, paper's auto-insert leaves
    // `second` as a child of `cp`, and `cp.pathData` would carry
    // BOTH halves — the first construction line would end up
    // holding the entire pre-slice geometry while the new tail line
    // also held the right half (duplicate).
    const secondPathData = second ? second.pathData : null;
    if (second) second.remove();
    const firstPathData = cp.pathData;
    cp.remove();
    if (!firstPathData) return;
    const firstId = target.constructionId;
    // Pre-mint the tail id so we can hand it to `setSelection` after
    // the commit. Generating ids inside the reducer is fine for the
    // commit itself but would leave us with no name to address the
    // new line by for the post-commit selection.
    const tailId = secondPathData ? newId("con") : null;
    const sliceScope = this.host.getScope();
    this.host.commit("Slice construction line", (proj) => {
      const updated = (proj.constructionLines ?? []).map((l) => {
        if (l.id !== firstId) return l;
        // `dropConstructionPrimitive` without `scope` skips the final
        // `canonicalizeConstructionLine` pass. The canonicalize step
        // would promote any 2-anchor open result back into a Line
        // primitive, which is the wrong outcome for slice: cutting a
        // line in two should leave both halves as polylines (the
        // user's mental model — they sliced a line, not "added a
        // gap"). All the metadata cleanup (cornerRounding, rawPoints,
        // smoothAnchors clear, auto-name rebrand to "Polyline N")
        // still runs because that part doesn't need scope.
        return dropConstructionPrimitive(
          l,
          proj,
          {
            pathData: firstPathData,
            kind: "polyline",
            smoothAnchors: undefined,
            bezierMemory: undefined,
          },
          { preserveOrigin: false },
        );
      });
      if (secondPathData && tailId) {
        // Tail half: build the polyline directly — same reason as
        // above, we skip `canonicalizeConstructionLine` so a 2-anchor
        // tail doesn't auto-promote into a Line primitive.
        updated.push({
          id: tailId,
          name: nextConstructionName(
            { ...proj, constructionLines: updated },
            "Polyline",
          ),
          pathData: secondPathData,
          kind: "polyline",
        });
        // Issue #275: the tail half is a fresh id, so the source's
        // offset subtree (which still references `firstId`) only
        // re-derives onto the first half. Deep-clone that subtree
        // and re-root the clones at the tail so both halves carry
        // their share of the offsets.
        const projWithTail = { ...proj, constructionLines: updated };
        const tailClones = duplicateOffsetSubtreeForSlice(
          projWithTail,
          firstId,
          tailId,
        );
        for (const c of tailClones) updated.push(c);
      }
      // Destroying geometry renumbers the line's anchors — which is what
      // anything TRACING it addressed its segments by — so re-point those
      // traces at what survived (#1029), which needs the project from both
      // sides of the edit. Anything unrepairable detaches instead.
      return repairOutlinesTracingLine(
        proj,
        {
          ...proj,
          constructionLines: updated,
          constraints: pruneInvalidCenterConstraints(updated, proj.constraints),
        },
        firstId,
        sliceScope,
      );
    });
    // Auto-selection on a single-point slice follows the open-curve
    // rule: walk the resulting pieces starting from the end of the
    // source line closest to where the cut started; exclude every
    // other piece beginning with the first one (the "main part"
    // adjacent to the cut start). With 2 pieces from an open cut
    // that yields exactly one cut-out — the piece sitting at the
    // far end of the source line from where the user clicked.
    // Closed-loop single cut produces 1 piece (just opens the
    // loop), which contributes nothing to the selection.
    if (tailId && secondPathData) {
      const cutStart = { x: loc.point.x, y: loc.point.y };
      const srcStart = getPathFirstPoint(firstPathData, scope);
      const srcEnd = getPathLastPoint(secondPathData, scope);
      // The piece *containing* the source endpoint closest to the
      // cut start is the first piece in the walk → excluded (the
      // "main part"). The OTHER piece is the cut-out (selected).
      // Default to selecting the tail half when endpoint lookup
      // fails, since that matches "excluded = first piece in the
      // natural pieces[] order."
      let cutOutId = tailId;
      if (srcStart && srcEnd) {
        const dStart = Math.hypot(
          cutStart.x - srcStart.x,
          cutStart.y - srcStart.y,
        );
        const dEnd = Math.hypot(cutStart.x - srcEnd.x, cutStart.y - srcEnd.y);
        if (dEnd < dStart) cutOutId = firstId;
      }
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        constructionIds: [cutOutId],
        constructionGroupIds: [],
      });
    } else {
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        constructionIds: [],
        constructionGroupIds: [],
      });
    }
  }

  // -------------------------------------------------------------------
  // Freehand cut (drag: split every crossed line at each crossing)
  // -------------------------------------------------------------------

  /** Freehand-cut commit (issue #193 follow-up). The user dragged
   *  the knife across the canvas; the polyline of pointer samples
   *  is a "knife stroke" that splits every visible construction
   *  line it crosses, at each crossing. Lines with N crossings on a
   *  single subpath emit N+1 pieces (the leftmost piece keeps the
   *  original construction-line id; the rest become new lines).
   *  Lines the cut path doesn't touch are left alone.
   *
   *  paper.js handles the De Casteljau math via `Path.splitAt`. We
   *  sort intersections from highest curve-offset to lowest so each
   *  split slices off the rightmost piece first — the leftmost
   *  portion's curve indices stay stable, no re-keying needed. The
   *  same splitAt-inserts-right-half-into-parent trick used by the
   *  single-point slice applies here too: each right half gets
   *  `.remove()`'d from the compound before we read the leftover
   *  `cp.pathData`, otherwise the first line would keep the full
   *  pre-cut geometry. */
  private performConstructionFreehandSlice(
    samples: Array<{ x: number; y: number }>,
  ): void {
    if (samples.length < 2) return;
    const scope = this.host.getScope();
    const cutPath = new scope.Path({ insert: false });
    for (const s of samples) cutPath.add(new scope.Point(s.x, s.y));
    if (cutPath.segments.length < 2) {
      cutPath.remove();
      return;
    }
    type LineResult = {
      constructionId: string;
      pieces: string[];
      // True iff any cut subpath of this line was closed before the
      // cut — drives which selection rule (open vs closed) applies.
      // Mixed-closedness compound lines (rare) follow the closed
      // rule if any cut subpath was closed.
      anyClosedCut: boolean;
      // Project-mm position of the intersection with the smallest
      // cut-path offset across all cut subpaths of this line — i.e.,
      // the first cut chronologically along the user's drag.
      // Used by the closed-curve selection rule to locate the
      // adjacent pieces. Null when no intersections were found
      // (shouldn't happen for lines in `results`).
      firstCutPoint: { x: number; y: number } | null;
    };
    const project = this.host.getProject();
    const results: LineResult[] = [];
    for (const line of projectConstructionLines(project)) {
      if (line.offsetSource) continue;
      if (line.visible === false) continue;
      if (this.host.isDerivedLineId(line.id)) continue;
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({
          pathData: line.pathData,
          insert: false,
        });
      } catch {
        continue;
      }
      const subpaths: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [cp as unknown as paper.Path];
      // Snapshot the original subpath identities so we can split
      // each one in place without confusing ourselves about whether
      // a child Path was original or a right-half paper inserted
      // during a previous iteration's splitAt.
      const originalSubpaths = new Set<paper.Path>(subpaths);
      let touchedAny = false;
      let anyClosedCut = false;
      let firstCutPoint: { x: number; y: number } | null = null;
      let firstCutOffset = Infinity;
      const allPieces: string[] = [];
      for (const sub of subpaths) {
        const inters = (sub as paper.Path).getIntersections(cutPath);
        if (inters.length === 0) {
          allPieces.push(sub.pathData);
          continue;
        }
        touchedAny = true;
        if (sub.closed) anyClosedCut = true;
        // Track the intersection with the smallest cut-path offset
        // across every cut subpath of this line. That's the cut
        // that landed FIRST under the user's moving knife — the
        // anchor point the closed-curve selection rule references.
        for (const inter of inters) {
          const cutOffset = cutPath.getOffsetOf(inter.point);
          if (typeof cutOffset === "number" && cutOffset < firstCutOffset) {
            firstCutOffset = cutOffset;
            firstCutPoint = { x: inter.point.x, y: inter.point.y };
          }
        }
        // Sort by descending offset so we slice off the rightmost
        // piece first; the leftover's curve indices stay stable.
        const sorted = [...inters].sort((a, b) => b.offset - a.offset);
        const rightHalves: paper.Path[] = [];
        for (const inter of sorted) {
          // Re-find the location on the (mutated) `sub` from the
          // intersection's point. Going by point rather than
          // curveIndex+t makes this robust to the segment
          // insertion that each prior splitAt performed.
          const loc = sub.getNearestLocation(inter.point);
          if (!loc) continue;
          const right = (sub as paper.Path).splitAt(loc);
          if (
            right &&
            right !== sub &&
            right.segments &&
            right.segments.length >= 2
          ) {
            // Detach from the compound so `cp.pathData` below only
            // reflects the leftmost piece of this subpath (plus
            // any unchanged sibling subpaths).
            right.remove();
            rightHalves.push(right as paper.Path);
          }
        }
        // `sub` is now the leftmost piece (segments may have
        // collapsed below 2 if a cut landed essentially at an
        // endpoint — drop those degenerates).
        if (sub.segments && sub.segments.length >= 2) {
          allPieces.push(sub.pathData);
        }
        // Right halves were collected rightmost-first; reverse so
        // pieces line up in path order from start to finish.
        for (let i = rightHalves.length - 1; i >= 0; i--) {
          const r = rightHalves[i]!;
          if (r.segments && r.segments.length >= 2) {
            allPieces.push(r.pathData);
          }
          r.remove();
        }
      }
      // Guard against stray right halves paper may have left inside
      // the compound (defensive — the explicit `.remove()` above
      // should have caught them already).
      if (cp.children) {
        for (const child of [...cp.children]) {
          if (!originalSubpaths.has(child as paper.Path)) {
            (child as paper.Path).remove();
          }
        }
      }
      cp.remove();
      if (touchedAny && allPieces.length > 0) {
        results.push({
          constructionId: line.id,
          pieces: allPieces,
          anyClosedCut,
          firstCutPoint,
        });
      }
    }
    cutPath.remove();
    if (results.length === 0) return;
    // Pre-mint ids for every new tail piece up front so the
    // post-commit selection has names to address. Generating ids
    // inside the reducer would leave us with no way to list the
    // exact new lines for `setSelection`.
    const tailIdsByConstructionId = new Map<string, string[]>();
    for (const r of results) {
      const tails: string[] = [];
      for (let i = 1; i < r.pieces.length; i++) tails.push(newId("con"));
      tailIdsByConstructionId.set(r.constructionId, tails);
    }
    const cutScope = this.host.getScope();
    const cutIds = results.map((r) => r.constructionId);
    this.host.commit("Freehand cut", (proj) => {
      let updated = proj.constructionLines ?? [];
      for (const r of results) {
        const firstPath = r.pieces[0]!;
        const firstId = r.constructionId;
        const tails = tailIdsByConstructionId.get(firstId) ?? [];
        updated = updated.map((l) =>
          l.id === firstId
            ? dropConstructionPrimitive(
                l,
                proj,
                {
                  pathData: firstPath,
                  kind: "polyline",
                  smoothAnchors: undefined,
                  bezierMemory: undefined,
                },
                { preserveOrigin: false },
              )
            : l,
        );
        for (let i = 1; i < r.pieces.length; i++) {
          updated.push({
            id: tails[i - 1]!,
            name: nextConstructionName(
              { ...proj, constructionLines: updated },
              "Polyline",
            ),
            pathData: r.pieces[i]!,
            kind: "polyline",
          });
        }
        // Issue #275: each tail piece needs its own copy of the
        // source's offset subtree — without this, the offsets only
        // re-derive onto the first half (the piece that kept the
        // original id) and the rest of the cut would silently lose
        // their share of the offset stack.
        for (let i = 1; i < r.pieces.length; i++) {
          const tailId = tails[i - 1]!;
          const projWithTails = { ...proj, constructionLines: updated };
          const tailClones = duplicateOffsetSubtreeForSlice(
            projWithTails,
            firstId,
            tailId,
          );
          for (const c of tailClones) updated.push(c);
        }
      }
      // Destroying geometry renumbers the line's anchors — which is what
      // anything TRACING it addressed its segments by — so re-point those
      // traces at what survived (#1029), which needs the project from both
      // sides of the edit. Anything unrepairable detaches instead.
      return repairOutlinesTracingLine(
        proj,
        {
          ...proj,
          constructionLines: updated,
          constraints: pruneInvalidCenterConstraints(updated, proj.constraints),
        },
        cutIds,
        cutScope,
      );
    });
    // Selection rule, per source line:
    //
    //  - **Open source**: walk the pieces starting from the end of
    //    the source line closest to where the cut started. Exclude
    //    every other piece beginning with the first (the piece
    //    nearest the cut start = the "main part" the user is keeping).
    //
    //  - **Closed source**: locate the first cut chronologically
    //    along the user's drag and the two pieces adjacent to it.
    //    Exclude the longer one first (the side of the loop the
    //    user is keeping), then alternate exclusion walking
    //    cyclically around.
    //
    // A source with only one piece (closed loop opened by a single
    // cut) contributes nothing to the selection — nothing was cut
    // out, the loop just opened. Length-tie cases keep the leftmost
    // piece as "main" via strict-greater comparison.
    const cutStart = samples[0]!;
    const selectedIds: string[] = [];
    for (const r of results) {
      const ids = [
        r.constructionId,
        ...(tailIdsByConstructionId.get(r.constructionId) ?? []),
      ];
      const N = ids.length;
      if (N < 2) continue;
      const excluded = new Set<number>();
      if (r.anyClosedCut) {
        // Closed: find the two pieces adjacent to the first cut.
        // Each piece's start and end anchor points are the cut
        // locations that bound it; the first cut point sits between
        // the piece whose END matches it and the piece whose START
        // matches it. Tolerance keeps matching robust against
        // floating-point drift in paper's serialised coordinates.
        const TOL = 0.01; // mm
        const fcp = r.firstCutPoint;
        let leftAdj = -1;
        let rightAdj = -1;
        if (fcp) {
          for (let i = 0; i < N; i++) {
            const s = getPathFirstPoint(r.pieces[i]!, scope);
            const e = getPathLastPoint(r.pieces[i]!, scope);
            if (
              s &&
              Math.hypot(s.x - fcp.x, s.y - fcp.y) < TOL &&
              rightAdj === -1
            ) {
              rightAdj = i;
            }
            if (
              e &&
              Math.hypot(e.x - fcp.x, e.y - fcp.y) < TOL &&
              leftAdj === -1
            ) {
              leftAdj = i;
            }
          }
        }
        let startIdx = 0;
        if (leftAdj >= 0 && rightAdj >= 0) {
          const lenL = measurePathDataLength(r.pieces[leftAdj]!, scope);
          const lenR = measurePathDataLength(r.pieces[rightAdj]!, scope);
          startIdx = lenL >= lenR ? leftAdj : rightAdj;
        }
        // Walk cyclically forward from startIdx, alternating
        // exclude / include starting with exclude. For odd N the
        // pattern can wrap around with two excluded neighbours;
        // that's fine — the rule says "alternate from this seed,"
        // not "produce a perfect 2-colouring."
        for (let k = 0; k < N; k++) {
          if (k % 2 === 0) excluded.add((startIdx + k) % N);
        }
      } else {
        // Open: walk from the source endpoint closest to cutStart.
        // pieces[0] starts at the source's natural start endpoint;
        // pieces[N-1] ends at the source's natural end endpoint.
        const startPt = getPathFirstPoint(r.pieces[0]!, scope);
        const endPt = getPathLastPoint(r.pieces[N - 1]!, scope);
        let walkFromIdx = 0;
        let walkStep = 1;
        if (startPt && endPt) {
          const dS = Math.hypot(cutStart.x - startPt.x, cutStart.y - startPt.y);
          const dE = Math.hypot(cutStart.x - endPt.x, cutStart.y - endPt.y);
          if (dE < dS) {
            walkFromIdx = N - 1;
            walkStep = -1;
          }
        }
        for (let k = 0; k < N; k++) {
          if (k % 2 === 0) {
            const idx = walkFromIdx + walkStep * k;
            if (idx >= 0 && idx < N) excluded.add(idx);
          }
        }
      }
      for (let i = 0; i < N; i++) {
        if (!excluded.has(i)) selectedIds.push(ids[i]!);
      }
    }
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionIds: selectedIds,
      constructionGroupIds: [],
    });
  }

  // -------------------------------------------------------------------
  // Drag-preview overlays + teardown
  // -------------------------------------------------------------------

  /** Tear down the freehand-cut drag state and the dashed preview
   *  it owns. Safe to call when no drag is active. */
  private clearSliceDrag(): void {
    if (this.sliceDrag?.previewItem) {
      this.sliceDrag.previewItem.remove();
    }
    (this.sliceDrag?.stroke ?? this.sliceStroke)?.end(this.host.getContext());
    this.sliceStroke = null;
    this.sliceDrag = null;
    this.sliceDragStart = null;
  }

  /** Tear down the swipe-delete drag state and its preview. Safe to
   *  call when no drag is active. */
  private clearDeleteDrag(): void {
    if (this.deleteDrag?.previewItem) {
      this.deleteDrag.previewItem.remove();
    }
    this.deleteDrag = null;
    this.deleteDragStart = null;
  }

  /** Redraw the red dashed stroke that traces the swipe-delete knife
   *  so the user sees what they're about to trim. Rebuilt each frame
   *  from `deleteDrag.samples`. */
  private refreshDeleteSwipePreview(): void {
    if (!this.deleteDrag) return;
    const scope = this.host.getScope();
    if (this.deleteDrag.previewItem) {
      this.deleteDrag.previewItem.remove();
      this.deleteDrag.previewItem = null;
    }
    if (this.deleteDrag.samples.length < 2) return;
    try {
      const preview = new scope.Path({ insert: true });
      for (const s of this.deleteDrag.samples) {
        preview.add(new scope.Point(s.x, s.y));
      }
      preview.strokeColor = new scope.Color(cssVar("--construction-broken"));
      preview.strokeWidth = 1.6;
      preview.strokeScaling = false;
      preview.dashArray = [6, 4];
      preview.fillColor = null;
      preview.data = { ephemeral: true };
      this.deleteDrag.previewItem = preview;
    } catch {
      /* preview only — swallow */
    }
  }

  /** Redraw the dashed polyline that traces the user's freehand
   *  cut so they can see what they're about to slice. Rebuilt each
   *  frame from `sliceDrag.samples`. */
  private refreshFreehandSlicePreview(): void {
    if (!this.sliceDrag) return;
    const scope = this.host.getScope();
    if (this.sliceDrag.previewItem) {
      this.sliceDrag.previewItem.remove();
      this.sliceDrag.previewItem = null;
    }
    const points = this.sliceDrag.stroke.points();
    if (points.length < 2) return;
    try {
      const preview = new scope.Path({ insert: true });
      for (const s of points) {
        preview.add(new scope.Point(s.x, s.y));
      }
      preview.strokeColor = new scope.Color(SLICE_CUT_STROKE());
      preview.strokeWidth = 1.4;
      preview.strokeScaling = false;
      preview.dashArray = [4, 3];
      preview.fillColor = null;
      preview.data = { ephemeral: true };
      this.sliceDrag.previewItem = preview;
    } catch {
      /* preview only — swallow */
    }
  }
}
